import { useEffect, useState } from 'react'

export type SlideSource = { src: string; poster?: string }

interface SlidingImagesProps {
  sources: SlideSource[]
  pauseMs?: number
  slideMs?: number
}

const isVideo = (src: string) => /\.(mp4|webm|ogg)(\?.*)?$/i.test(src)

const mediaStyle: React.CSSProperties = {
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  display: 'block',
}

function Slide({ src, poster }: SlideSource) {
  if (isVideo(src)) {
    return (
      <video
        src={src}
        poster={poster}
        autoPlay
        muted
        loop
        playsInline
        draggable={false}
        style={{ ...mediaStyle, position: 'absolute', inset: 0 }}
      />
    )
  }
  return <img src={src} alt="" draggable={false} style={mediaStyle} />
}

function SlidingImages({ sources, pauseMs = 1500, slideMs = 500 }: SlidingImagesProps) {
  const [curr, setCurr] = useState(0)
  const [isAnimating, setIsAnimating] = useState(false)
  const isSingle = sources.length <= 1
  const next = (curr + 1) % sources.length

  useEffect(() => {
    if (isSingle) return
    const t = setTimeout(() => setIsAnimating(true), pauseMs)
    return () => clearTimeout(t)
  }, [curr, isSingle, pauseMs])

  if (isSingle) {
    return (
      <div style={{ position: 'relative', width: '100%', height: '100%' }}>
        <Slide {...sources[0]} />
      </div>
    )
  }

  return (
    <div
      style={{ position: 'relative', width: '100%', height: '100%', overflow: 'hidden', userSelect: 'none' }}
      onDragStart={e => e.preventDefault()}
    >
      <div
        style={{
          position: 'absolute',
          inset: 0,
          transform: isAnimating ? 'translateX(-100%)' : 'translateX(0)',
          transition: isAnimating ? `transform ${slideMs}ms cubic-bezier(0.4, 0, 0.2, 1)` : 'none',
        }}
        onTransitionEnd={() => {
          setCurr(next)
          setIsAnimating(false)
        }}
      >
        <Slide {...sources[curr]} />
      </div>
      <div
        style={{
          position: 'absolute',
          inset: 0,
          transform: isAnimating ? 'translateX(0)' : 'translateX(100%)',
          transition: isAnimating ? `transform ${slideMs}ms cubic-bezier(0.4, 0, 0.2, 1)` : 'none',
        }}
      >
        <Slide {...sources[next]} />
      </div>
    </div>
  )
}

export default SlidingImages
